import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Image,
    Video,
    Upload,
    Trash2,
    Search,
    Grid,
    List,
    Loader2,
    X,
    FileImage,
    FileVideo, Send } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function MediaLibrary() {
    const { api } = useAuth();
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const [selectedForPost, setSelectedForPost] = useState([]);
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [mediaToDelete, setMediaToDelete] = useState(null);

    const fetchMedia = useCallback(async () => {
        setLoading(true);
        try {
            const params = filter !== 'all' ? `?file_type=${filter}` : '';
            const response = await api.get(`/media${params}`);
            setMedia(response.data);
        } catch (error) {
            console.error('Failed to fetch media:', error);
            toast.error('Failed to load media');
        } finally {
            setLoading(false);
        }
    }, [api, filter]);

    useEffect(() => {
        fetchMedia();
    }, [fetchMedia]);

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploading(true);
        const uploadPromises = Array.from(files).map(async (file) => {
            const formData = new FormData();
            formData.append('file', file);
            try {
                await api.post('/media/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                return { success: true, name: file.name };
            } catch (error) {
                return { success: false, name: file.name, error };
            }
        });

        const results = await Promise.all(uploadPromises);
        const successful = results.filter(r => r.success);
        const failed = results.filter(r => !r.success);

        if (successful.length > 0) {
            toast.success(`Uploaded ${successful.length} file(s)`);
            fetchMedia();
        }
        if (failed.length > 0) {
            toast.error(`Failed to upload ${failed.length} file(s)`);
        }

        setUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const togglePostSelection = (id) => {
        setSelectedForPost(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const startNewPost = () => {
        if (selectedForPost.length === 0) return;
        navigate(`/compose?media=${selectedForPost.join(',')}`);
    };

    const handleDelete = async () => {
        if (!mediaToDelete) return;
        try {
            await api.delete(`/media/${mediaToDelete.id}`);
            toast.success('Media deleted');
            setMedia(prev => prev.filter(m => m.id !== mediaToDelete.id));
            setDeleteDialogOpen(false);
            setMediaToDelete(null);
            if (selectedMedia?.id === mediaToDelete.id) {
                setSelectedMedia(null);
            }
        } catch (error) {
            console.error('Failed to delete media:', error);
            toast.error('Failed to delete media');
        }
    };

    const filteredMedia = media.filter(item => {
        if (search) {
            return item.original_filename.toLowerCase().includes(search.toLowerCase());
        }
        return true;
    });

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="media-library-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Media Library</h1>
                    <p className="text-muted-foreground mt-1">
                        Manage your images and videos
                    </p>
                </div>
                <div className="flex gap-2">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        className="hidden"
                        onChange={handleUpload}
                        data-testid="file-input"
                    />
                    <Button
                        className="gradient-bg"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="upload-btn"
                    >
                        {uploading ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Upload className="mr-2 h-4 w-4" />
                        )}
                        Upload Media
                    </Button>
                    <Button
                        variant="default"
                        onClick={startNewPost}
                        disabled={selectedForPost.length === 0}
                        data-testid="use-in-post-btn"
                    >
                        <Send className="mr-2 h-4 w-4" />
                        {selectedForPost.length > 0 ? `Use ${selectedForPost.length} in new post` : 'Select to post'}
                    </Button>
                </div>
            </div>

            <Card>
                <CardHeader className="pb-4">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <Tabs value={filter} onValueChange={setFilter}>
                            <TabsList>
                                <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
                                <TabsTrigger value="image" data-testid="filter-images">
                                    <Image className="mr-2 h-4 w-4" />
                                    Images
                                </TabsTrigger>
                                <TabsTrigger value="video" data-testid="filter-videos">
                                    <Video className="mr-2 h-4 w-4" />
                                    Videos
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                        <div className="flex gap-2 w-full sm:w-auto">
                            <div className="relative flex-1 sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search media..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-10"
                                    data-testid="search-input"
                                />
                            </div>
                            <div className="flex border border-border rounded-md">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(viewMode === 'grid' && 'bg-muted')}
                                    onClick={() => setViewMode('grid')}
                                >
                                    <Grid className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className={cn(viewMode === 'list' && 'bg-muted')}
                                    onClick={() => setViewMode('list')}
                                >
                                    <List className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center h-[400px]">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : filteredMedia.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                            <FileImage className="h-16 w-16 mb-4 opacity-50" />
                            <p className="text-lg font-medium">No media found</p>
                            <p className="text-sm">Upload images or videos to get started</p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Upload className="mr-2 h-4 w-4" />
                                Upload Media
                            </Button>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {filteredMedia.map(item => (
                                <div
                                    key={item.id}
                                    className="media-grid-item group"
                                    onClick={() => setSelectedMedia(item)}
                                    data-testid={`media-grid-item-${item.id}`}
                                >
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); togglePostSelection(item.id); }}
                                        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedForPost.includes(item.id) ? 'bg-primary border-primary text-primary-foreground' : 'bg-background/80 border-muted-foreground/50 hover:border-primary'}`}
                                        data-testid={`select-${item.id}`}
                                    >
                                        {selectedForPost.includes(item.id) && <span className="text-xs font-bold">{selectedForPost.indexOf(item.id) + 1}</span>}
                                    </button>
                                    {item.file_type === 'image' ? (
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                                            alt={item.original_filename}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-muted flex items-center justify-center">
                                            <Video className="h-8 w-8 text-muted-foreground" />
                                        </div>
                                    )}
                                    <div className="media-overlay">
                                        <Button
                                            variant="destructive"
                                            size="icon"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setMediaToDelete(item);
                                                setDeleteDialogOpen(true);
                                            }}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                                        <p className="text-xs text-white truncate">
                                            {item.original_filename}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredMedia.map(item => (
                                <div
                                    key={item.id}
                                    className="flex items-center gap-4 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                                    onClick={() => setSelectedMedia(item)}
                                    data-testid={`media-list-item-${item.id}`}
                                >
                                    <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); togglePostSelection(item.id); }}
                                        className={`absolute top-2 left-2 z-10 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${selectedForPost.includes(item.id) ? 'bg-primary border-primary text-primary-foreground' : 'bg-background border-muted-foreground/50 hover:border-primary'}`}
                                        data-testid={`select-${item.id}`}
                                    >
                                        {selectedForPost.includes(item.id) && <span className="text-[10px] font-bold">{selectedForPost.indexOf(item.id) + 1}</span>}
                                    </button>
                                    <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                                        {item.file_type === 'image' ? (
                                            <img
                                                src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                                                alt={item.original_filename}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <Video className="h-6 w-6 text-muted-foreground" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">{item.original_filename}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {formatFileSize(item.file_size)} • {format(new Date(item.created_at), 'MMM d, yyyy')}
                                        </p>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-destructive hover:text-destructive"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMediaToDelete(item);
                                            setDeleteDialogOpen(true);
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Media Details</DialogTitle>
                    </DialogHeader>
                    {selectedMedia && (
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                                {selectedMedia.file_type === 'image' ? (
                                    <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${selectedMedia.url}`}
                                        alt={selectedMedia.original_filename}
                                        className="w-full h-full object-contain"
                                    />
                                ) : (
                                    <video
                                        src={`${process.env.REACT_APP_BACKEND_URL}${selectedMedia.url}`}
                                        controls
                                        className="w-full h-full"
                                    />
                                )}
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Filename</p>
                                    <p className="font-medium">{selectedMedia.original_filename}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Type</p>
                                    <p className="font-medium capitalize">{selectedMedia.file_type}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Size</p>
                                    <p className="font-medium">{formatFileSize(selectedMedia.file_size)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Uploaded</p>
                                    <p className="font-medium">
                                        {format(new Date(selectedMedia.created_at), 'PPP p')}
                                    </p>
                                </div>
                                <Button
                                    variant="destructive"
                                    className="w-full"
                                    onClick={() => {
                                        setMediaToDelete(selectedMedia);
                                        setDeleteDialogOpen(true);
                                    }}
                                >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Media</DialogTitle>
                    </DialogHeader>
                    <p>Are you sure you want to delete "{mediaToDelete?.original_filename}"?</p>
                    <p className="text-sm text-muted-foreground">
                        This action cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
